import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DURUM_BADGE,
  DURUM_LABELS,
  getServisKaydiBaglantiLabel,
} from '../utils/onServisKaydiHelpers';
import './OnServisDetailModal.css';

const formatTarih = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val || '—';
  return d.toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' });
};

function DurumBadge({ durum }) {
  const style = DURUM_BADGE[durum] || { bg: '#f1f5f9', color: '#334155' };
  return (
    <span
      className="osd-header-badge"
      style={{ backgroundColor: style.bg, color: style.color }}
    >
      {DURUM_LABELS[durum] || durum}
    </span>
  );
}

const Field = ({ label, children }) => (
  <div className="osd-field">
    <span className="osd-field-label">{label}</span>
    <div className="osd-field-value">{children}</div>
  </div>
);

const OnServisDetailModal = ({
  detail,
  loading,
  error,
  onClose,
  onDurumChange,
  onGoToServisKaydi,
  savingDurum = false,
}) => {
  const [durumDraft, setDurumDraft] = useState('');

  useEffect(() => {
    setDurumDraft(detail?.durum || '');
  }, [detail?.durum, detail?.id]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!detail && !loading) return null;

  const musteriAd = detail
    ? `${detail.musteriAd || ''} ${detail.musteriSoyad || ''}`.trim() || '—'
    : 'Ön Servis Kaydı';

  const handleDurumSave = () => {
    if (!detail?.id || !durumDraft || durumDraft === detail.durum) return;
    onDurumChange?.(detail.id, durumDraft);
  };

  return (
    <div className="modal-overlay on-servis-detail-modal" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <i className="fas fa-clipboard-list" style={{ marginRight: 8, color: '#d97706' }} />
            {musteriAd}
            {detail?.durum ? <DurumBadge durum={detail.durum} /> : null}
          </h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner" />
              <div className="loading-text">Detay yükleniyor</div>
            </div>
          )}

          {!loading && error && (
            <div className="error-state">{error}</div>
          )}

          {!loading && !error && detail && (
            <div className="osd-content">
              <section className="osd-section">
                <div className="osd-section-title">
                  <i className="fas fa-user" />
                  Müşteri Bilgileri
                </div>
                <div className="osd-grid">
                  <Field label="Müşteri">{musteriAd}</Field>
                  <Field label="Telefon">{detail.telefon || '—'}</Field>
                  <Field label="Ürün">{detail.urunAdi || '—'}</Field>
                  <Field label="Seri No">{detail.seriNumarasi || '—'}</Field>
                  <Field label="Bayi">{detail.bayiAdi || '—'}</Field>
                  <Field label="Oluşturulma">{formatTarih(detail.createdAt)}</Field>
                  <Field label="OTP Doğrulama">{formatTarih(detail.otpDogrulandiAt)}</Field>
                </div>
              </section>

              <section className="osd-section">
                <div className="osd-section-title">
                  <i className="fas fa-link" />
                  Bağlı Servis Kaydı
                </div>
                {detail.servisKaydiId ? (
                  <>
                    <Link
                      to={`/service-records/${detail.servisKaydiId}/update`}
                      className="osd-sk-link"
                      onClick={onClose}
                    >
                      <i className="fas fa-clipboard-list" />
                      {detail.servisKaydiNumarasi || detail.servisKaydiId}
                      <i className="fas fa-arrow-right" />
                    </Link>
                    {detail.servisKaydiDurum && (
                      <p className="osd-sk-meta">
                        {getServisKaydiBaglantiLabel(detail.servisKaydiDurum)}
                        <span className="osd-muted"> ({detail.servisKaydiDurum})</span>
                      </p>
                    )}
                    {detail.servisKaydiBaglandiAt && (
                      <p className="osd-muted">Bağlandı: {formatTarih(detail.servisKaydiBaglandiAt)}</p>
                    )}
                  </>
                ) : (
                  <p className="osd-muted">Henüz servis kaydı oluşturulmadı</p>
                )}
              </section>

              <section className="osd-section">
                <div className="osd-section-title">
                  <i className="fas fa-flag" />
                  Durum Güncelle
                </div>
                <div className="osd-durum-row">
                  <select
                    className="osd-durum-select"
                    value={durumDraft}
                    onChange={(e) => setDurumDraft(e.target.value)}
                    disabled={savingDurum}
                  >
                    {Object.entries(DURUM_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleDurumSave}
                    disabled={savingDurum || durumDraft === detail.durum}
                  >
                    {savingDurum ? 'Kaydediliyor...' : 'Durumu Kaydet'}
                  </button>
                </div>
                {detail.servisKaydiId && (
                  <p className="osd-warning">
                    Bu ön kayıt bir servis kaydına bağlı. Durum değişikliği servis kaydını etkilemez;
                    bağlantı bilgisi detayda görünmeye devam eder.
                  </p>
                )}
              </section>
            </div>
          )}
        </div>

        {!loading && detail && (
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Kapat
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onGoToServisKaydi?.(detail)}
            >
              <i className={`fas ${detail.servisKaydiId ? 'fa-external-link-alt' : 'fa-plus'}`} style={{ marginRight: 8 }} />
              {detail.servisKaydiId ? 'Servis Kaydına Git' : 'Servis Kaydı Oluştur'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnServisDetailModal;
